'use client';

import { useState } from 'react';
import { Mail, ShieldCheck, X } from 'lucide-react';
import { apiService } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

type EmailVerificationGateProps = {
  open: boolean;
  onClose: () => void;
  onVerified?: () => Promise<void> | void;
};

export default function EmailVerificationGate({ open, onClose, onVerified }: EmailVerificationGateProps) {
  const { user, refreshUser } = useAuth();
  const [otp, setOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [useNewEmail, setUseNewEmail] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying'>('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSendOtp = async () => {
    try {
      setError('');
      setMessage('');
      setStatus('sending');

      const candidateEmail = newEmail.trim();
      const response: any = await apiService.requestEmailVerification(
        useNewEmail && candidateEmail ? candidateEmail : undefined
      );

      if (response?.success) {
        setStatus('sent');
        setMessage(response?.message || 'OTP has been sent to your email.');
      } else {
        setStatus('idle');
        setError(response?.message || 'Failed to send OTP.');
      }
    } catch (err: any) {
      setStatus('idle');
      setError(err?.message || 'Failed to send OTP.');
    }
  };

  const handleVerifyOtp = async () => {
    try {
      if (!otp.trim()) {
        setError('Please enter OTP.');
        return;
      }

      setError('');
      setMessage('');
      setStatus('verifying');

      const response: any = await apiService.verifyEmailChange(otp.trim());
      if (!response?.success) {
        setStatus('sent');
        setError(response?.message || 'Invalid OTP.');
        return;
      }

      await refreshUser();
      if (onVerified) {
        await onVerified();
      }
      onClose();
      setStatus('idle');
      setOtp('');
      setNewEmail('');
      setUseNewEmail(false);
    } catch (err: any) {
      setStatus('sent');
      setError(err?.message || 'Failed to verify OTP.');
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-dark-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary-600" />
            Verify Email To Continue
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You can browse services without verification, but booking requires OTP verification.
          </p>

          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
            Current email: <strong>{user?.email || 'Not available'}</strong>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={useNewEmail}
              onChange={(e) => setUseNewEmail(e.target.checked)}
              className="w-4 h-4"
            />
            Change email address before verification
          </label>

          {useNewEmail && (
            <div>
              <label className="block text-sm font-medium mb-1">New Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="input"
                placeholder="new-email@example.com"
              />
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={status === 'sending' || status === 'verifying'}
              className="btn btn-primary flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              {status === 'sending' ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>

          {(status === 'sent' || status === 'verifying') && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">OTP Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input text-center tracking-widest"
                maxLength={6}
                placeholder="000000"
              />
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={status === 'verifying' || otp.length !== 6}
                className="btn btn-primary"
              >
                {status === 'verifying' ? 'Verifying...' : 'Verify OTP'}
              </button>
            </div>
          )}

          {message && <p className="text-sm text-green-600 dark:text-green-400">{message}</p>}
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      </div>
    </div>
  );
}
